/* eslint-disable prettier/prettier */
/* eslint-disable prefer-destructuring */
import { Type_RefFuncKrpano } from '@components/Krpano/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCollaborationMode,
  useFeatureCommentGoterest,
} from '../global/TourStats';
import { selectListCommentGoterestSelected } from '../selectors';
import { actions } from '../slice';
import { I_CommentGoterest } from '../types';
import {
  I_PayloadAddCommentGoterest,
  createCommentApi,
  toggleLikeCommentApi,
} from 'service/masterDataApi';
import { selectInfoUser } from '@containers/App/selectors';
import { useValidatePermission } from '@utils/hooks/useValidatePermission';

export const useCommentGoterest = () => {
  const dispatch = useDispatch();

  const infoUser = useSelector(selectInfoUser);
  const listCommentGoterestSelected = useSelector(
    selectListCommentGoterestSelected,
  );

  const listCommentGoterestGroupByParentRef = useRef<I_CommentGoterest[]>([]);

  const { onValidatePermission } = useValidatePermission();
  const [krpanoInstance] = useCollaborationMode('krpanoRef');
  const [, setIsShowDialogCreateCommentScene] = useFeatureCommentGoterest(
    'isShowDialogCreateCommentScene',
  );
  const [, setLoading] = useFeatureCommentGoterest('loading');

  const listCommentGoterestGroupByParent = useMemo(
    () =>
      listCommentGoterestSelected
        ?.filter(item => !item.parentId)
        ?.slice()
        ?.sort((a, b) => a.dateCreated.localeCompare(b.dateCreated))
        ?.map(item => ({
          ...item,
          children:
            listCommentGoterestSelected
              ?.filter(el => el.parentId === item.id)
              ?.map(el => ({
                ...el,
              }))
              ?.slice()
              ?.sort((a, b) => b.dateCreated.localeCompare(a.dateCreated)) ||
            [],
        })) || [],
    [listCommentGoterestSelected],
  );

  useEffect(() => {
    listCommentGoterestGroupByParentRef.current =
      listCommentGoterestGroupByParent;
  }, [listCommentGoterestGroupByParent]);

  const onCreateCommentGoterest = useCallback(
    async (
      payload: I_PayloadAddCommentGoterest,
      instanceKrpano: Type_RefFuncKrpano = krpanoInstance,
      cb = () => {},
    ) => {
      await onValidatePermission(async () => {
        setLoading(true);
        try {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (key !== 'images') formData.append(key, value);
            else
              value.forEach(file => {
                formData.append('images[]', file);
              });
          });
          try {
            const { data: response } = await createCommentApi(formData);
            const data = { ...response, liked: false };
            dispatch(actions.createCommentGoterest(data));

            let newData;
            if (data.parentId) {
              const commentSelected =
                listCommentGoterestGroupByParentRef.current.filter(
                  item => item.id === data.parentId,
                )[0];
              newData = {
                ...commentSelected,
                children: [{ ...data }, ...(commentSelected.children || [])],
              };
              if (instanceKrpano)
                instanceKrpano.onReplaceCommentGoterest(newData);
            } else {
              newData = {
                ...data,
              };
              newData.children = [];
              if (instanceKrpano) instanceKrpano.onAddCommentGoterest(newData);
            }
            setIsShowDialogCreateCommentScene(false);
            dispatch(actions.onChangeIsOnModeCreateCommentGoterest(false));
          } catch (err) {
            console.log(err);
          }
        } finally {
          setLoading(false);
          if (cb) await cb();
        }
      });
    },
    [
      dispatch,
      setLoading,
      krpanoInstance,
      onValidatePermission,
      setIsShowDialogCreateCommentScene,
    ],
  );

  const onToggleLikeCommentGoterest = useCallback(
    async (payload, instanceKrpano: Type_RefFuncKrpano = krpanoInstance) => {
      await onValidatePermission(async () => {
        setLoading(true);
        try {
          const { data: response } = await toggleLikeCommentApi(payload);

          const data = {
            ...response.data,
            liked: response.data.listUserIdLikes.indexOf(infoUser.id) !== -1,
          };
          dispatch(actions.updateCommentGoterest(data));

          let newData;
          if (data.parentId) {
            const commentSelected =
              listCommentGoterestGroupByParentRef.current.filter(
                item => item.id === data.parentId,
              )[0];
            newData = commentSelected;
            newData.children = newData.children.map(item =>
              item.id === data.id
                ? {
                    ...data,
                    children: [],
                  }
                : item,
            );
          } else {
            const commentSelected =
              listCommentGoterestGroupByParentRef.current.filter(
                item => item.id === data.id,
              )[0];
            newData = {
              ...commentSelected,
              ...data,
            };
          }
          if (instanceKrpano) instanceKrpano.onReplaceCommentGoterest(newData);
        } finally {
          setLoading(false);
        }
      });
    },
    [krpanoInstance, onValidatePermission, setLoading, infoUser, dispatch],
  );

  return useMemo(
    () => ({
      onCreateCommentGoterest,
      onToggleLikeCommentGoterest,
      listCommentGoterestSelected,
      listCommentGoterestGroupByParent,
    }),
    [
      onCreateCommentGoterest,
      onToggleLikeCommentGoterest,
      listCommentGoterestSelected,
      listCommentGoterestGroupByParent,
    ],
  );
};
